import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import api from '../api/axios';

const Login = () => {
    // Función para limpiar siempre la caché de imágenes
    const [cacheBuster] = useState(() => Date.now());
    const getCleanImageUrl = (url) => {
        if (!url) return '';
        return `${url}${url.includes('?') ? '&' : '?'}v=${cacheBuster}`;
    };

    const { login } = useAuth();
    const { config, loading: configLoading } = useConfig();
    const [loginInput, setLoginInput] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('landing'); // 'landing' or 'login'
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);
        const result = await login(loginInput);
        if (result.success) {
            try {
                await api.post('/sessions/log', {
                    page_name: 'login',
                    action_type: 'login'
                });
            } catch (err) {
                console.error("Failed to log login action", err);
            }
            navigate('/');
        } else {
            setError('Valide su información, si no se encuentra registrado, lo invitamos a registrarse.');
            setIsLoading(false);
        }
    };

    if (configLoading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
            </div>
        );
    }

    const pageDesign = config?.pageDesigns?.login || {};

    const bottomBg = isMobile
        ? getCleanImageUrl('https://ftp.pcnpublicidad.com/amgen/cel_abajo.png')
        : getCleanImageUrl('https://ftp.pcnpublicidad.com/amgen/web_abajo.png');

    let logoUrl = '';
    if (isMobile) {
        logoUrl = getCleanImageUrl('https://ftp.pcnpublicidad.com/amgen/cel_arriba.png');
    } else {
        logoUrl = getCleanImageUrl('https://ftp.pcnpublicidad.com/amgen/web_arriba.png');
    }

    const primaryColor = config?.primaryColor || "#0a58ca";
    const secondaryColor = config?.secondaryColor || "#ffa800";

    const containerStyle = {
        backgroundColor: '#ffffff',
        color: pageDesign.globalFontColor || '#000000'
    };

    return (
        <div
            style={containerStyle}
            className="min-h-screen flex flex-col relative overflow-x-hidden font-sans items-center"
        >
            {/* Bottom Background Layer removed to maintain solid white background */}

            {/* Configured Global Logo */}
            <div className="relative z-50 w-full flex justify-center pointer-events-none flex-shrink-0">
                <img
                    src={logoUrl}
                    alt="Logo Global"
                    className="w-full h-auto object-contain drop-shadow-md"
                />
            </div>

            {/* Removed dynamic visual elements to prevent obsolete text rendering */}

            {/* Content Area */}
            <div className="w-full max-w-sm px-6 z-10 flex flex-col justify-center flex-grow mt-2 mb-4 animate-fadeIn">

                {viewMode === 'landing' && (
                    <div className="flex flex-col gap-5 w-full">
                        <button
                            onClick={() => setViewMode('login')}
                            style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                            className="w-full text-white py-4 px-6 rounded-md font-black uppercase tracking-widest text-[14px] sm:text-[16px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            INICIAR
                        </button>

                        <button
                            onClick={() => navigate('/register')}
                            style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                            className="w-full text-white py-4 px-6 rounded-md font-black uppercase tracking-widest text-[14px] sm:text-[16px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                        >
                            REGISTRARSE
                        </button>

                        <div className="w-full flex justify-center mt-4 pointer-events-none">
                            <img src={`https://ftp.pcnpublicidad.com/amgen/titulo2.png?v=${Date.now()}`} alt="Sponsor" className="w-[85%] max-w-[300px] object-contain drop-shadow-md" />
                        </div>
                    </div>
                )}

                {viewMode === 'login' && (
                    <div
                        style={{ borderColor: primaryColor }}
                        className="w-full bg-white p-8 rounded-2xl border-2 shadow-[0_15px_30px_rgba(0,0,0,0.1)] transition-all duration-500"
                    >
                        <div className="text-center mb-6">
                            <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tighter">Acceso</h3>
                            <p className="text-xs text-slate-500 uppercase tracking-widest mt-1">Ingresa tus datos</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div className="space-y-2">
                                <label
                                    style={{ color: primaryColor }}
                                    className="text-[11px] uppercase font-black tracking-wide pl-1 block text-left"
                                >
                                    {config?.registration_fields?.identification === false ? 'Correo Electrónico' : 'Identificación'}
                                </label>
                                <input
                                    className="w-full bg-white border-2 border-slate-200 focus:border-[#0a58ca] p-4 rounded-lg focus:outline-none transition-all font-bold text-slate-800 text-center shadow-inner placeholder:text-slate-300"
                                    style={{ '--tw-border-opacity': '1', ':focus': { borderColor: primaryColor } }}
                                    placeholder={config?.registration_fields?.identification === false ? 'tu@email.com' : 'Ej: 12345678'}
                                    value={loginInput}
                                    onChange={(e) => setLoginInput(e.target.value)}
                                    required
                                />
                            </div>


                            <button
                                type="submit"
                                style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                                className="w-full text-white py-4 rounded-md font-black uppercase tracking-widest text-[13px] shadow-lg hover:scale-[1.02] active:scale-95 transition-all duration-300 mt-4"
                            >
                                {isLoading ? 'Accediendo...' : 'Ingresar'}
                            </button>

                            {error && (
                                <div className="space-y-3 mt-4">
                                    <div className="bg-red-50 text-red-600 p-3 rounded-lg text-[10px] font-black uppercase tracking-wider border border-red-200 text-center">
                                        {error}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/register')}
                                        style={{ background: 'linear-gradient(to right, #033f83, #079acf, #65bade)' }}
                                        className="w-full text-white py-3 px-6 rounded-md font-black uppercase tracking-widest text-[13px] shadow-[0_10px_20px_rgba(0,0,0,0.15)] hover:scale-[1.03] active:scale-95 transition-all duration-300 text-center"
                                    >
                                        REGISTRARSE
                                    </button>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => setViewMode('landing')}
                                className="w-full text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-slate-800 transition-colors pt-2 block"
                            >
                                ← Volver
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Gallery perspective image placed inline instead of fixed background */}
            {(viewMode === 'landing' || viewMode === 'login') && (
                <div className="w-full pointer-events-none mt-2 relative -mx-4 sm:-mx-6 max-w-[100vw] overflow-hidden flex justify-center">
                    <img
                        src={window.innerWidth < 640 ? "https://ftp.pcnpublicidad.com/amgen/cel_abajo.png" : "https://ftp.pcnpublicidad.com/amgen/web_abajo.png"}
                        alt="Gallery Background"
                        className="w-full object-cover object-top"
                    />
                </div>
            )}

            {pageDesign?.showLegalText && pageDesign?.legalTextUrl && (
                <footer className="w-full p-4 sm:p-6 flex-shrink-0 flex justify-center items-center z-40 select-none bg-transparent">
                    <img
                        src={getCleanImageUrl('https://ftp.pcnpublicidad.com/amgen/legal_general.png')}
                        alt=""
                        className="max-w-[90vw] md:max-w-2xl h-auto object-contain pointer-events-none"
                        onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.style.display = 'none'; }}
                    />
                </footer>
            )}
        </div>
    );
};

export default Login;
